import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { openUserProfile } from 'features/users/openUserProfile';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import axiosInstance from 'shared/services/api/axiosInstance';
import { useQueryClient } from '@tanstack/react-query';
import UsersFilterBar from 'features/activity/components/UsersFilterBar';
import UsersTable from 'features/activity/components/UsersTable';
import UserColumnConfigModal from 'features/activity/components/UserColumnConfigModal';
import GroupsFilterBar from 'features/activity/components/GroupsFilterBar';
import GroupsTable from 'features/activity/components/GroupsTable';
import GroupColumnConfigModal from 'features/activity/components/GroupColumnConfigModal';
import { FIXED_GROUP_COLUMNS } from 'features/activity/config/groupColumns';
import { usePreferences } from 'shared/context/preferencesContext';
import { RefreshCw } from 'lucide-react';
import TableFooter from 'shared/components/tables/TableFooter';

const Sessions = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Users (with their group tabs) is the only view left. Events and Sessions
  // were the browser SDK's pageview side, retired 28 Oct 2026; the routes
  // send any old link here.
  const subpage = params.subpage || 'users';
  useBreadcrumb([{ label: 'Users' }]);

  // Only an /activity/<old view> address redirects. `/:team/agent-analytics/
  // :subpage` names its param `subpage` too, and this page stays mounted for one
  // render after a sidebar click to Evals or Capabilities: without the path
  // check it read that click as an old Activity link and sent it back here.
  const onActivityPath = location.pathname.includes('/activity/');
  useEffect(() => {
    if (subpage !== 'users' && onActivityPath) {
      const team = params.team || localStorage.getItem('selectedTeam') || '';
      navigate(`/${encodeURIComponent(team)}/activity/users`, { replace: true });
    }
  }, [subpage, onActivityPath, params.team, navigate]);

  const chainContext = 'Users';
  
  // Users state
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [modularFiltersUsers, setModularFiltersUsers] = useState([]);
  const [appliedModularFiltersUsers, setAppliedModularFiltersUsers] = useState([]);
  const [userTimeFilter, setUserTimeFilter] = useState(null);
  const [userSelectedTimePreset, setUserSelectedTimePreset] = useState('30d');
  const [lastUserRefreshTime, setLastUserRefreshTime] = useState(null);
  const [isUserColumnModalOpen, setIsUserColumnModalOpen] = useState(false);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);

  // Group tabs state
  const [groupKeys, setGroupKeys] = useState([]); // [{group_key, count}]
  const [activeGroupTab, setActiveGroupTab] = useState(null); // null = Users tab
  const [groups, setGroups] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [groupTimeFilter, setGroupTimeFilter] = useState(null);
  const [groupSelectedTimePreset, setGroupSelectedTimePreset] = useState('30d');
  const [groupModularFilters, setGroupModularFilters] = useState([]);
  const [appliedGroupModularFilters, setAppliedGroupModularFilters] = useState([]);
  const [loadingMoreGroups, setLoadingMoreGroups] = useState(false);
  const [isGroupColumnModalOpen, setIsGroupColumnModalOpen] = useState(false);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [lastGroupRefreshTime, setLastGroupRefreshTime] = useState(null);
  /** Distinct custom_properties keys for the active group_key (from API + merged locally) */
  const [groupCustomPropertyKeysFromApi, setGroupCustomPropertyKeysFromApi] = useState([]);
  
  // Shared state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(localStorage.getItem("selectedTeam") || '');

  // ---------------------------------------------------------------------------
  // Back/forward UX: persist Users/Groups view state in the current history entry
  // and hydrate it on mount. React Query keeps the DATA cache; this keeps the UI
  // controls (tab, search, filters, time ranges) stable across navigation.
  // ---------------------------------------------------------------------------

  const restoringFromHistoryRef = useRef(false);

  // Hydrate from history state when returning to /activity/users
  useEffect(() => {
    if (chainContext !== 'Users') return;
    // Prefer history state (popstate/back-forward) over location.state
    const histUsr =
      window.history.state && typeof window.history.state === 'object'
        ? window.history.state.usr
        : null;
    const st = (histUsr && typeof histUsr === 'object')
      ? histUsr
      : (location.state && typeof location.state === 'object' ? location.state : null);

    const saved = st?.activityUsersState;
    if (!saved || typeof saved !== 'object') return;

    restoringFromHistoryRef.current = true;
    try {
      if (typeof saved.activeGroupTab !== 'undefined') setActiveGroupTab(saved.activeGroupTab);
      if (typeof saved.searchTerm === 'string') setSearchTerm(saved.searchTerm);
      if (typeof saved.groupSearchTerm === 'string') setGroupSearchTerm(saved.groupSearchTerm);

      if (saved.userTimeFilter) setUserTimeFilter(saved.userTimeFilter);
      if (typeof saved.userSelectedTimePreset === 'string') setUserSelectedTimePreset(saved.userSelectedTimePreset);
      if (Array.isArray(saved.modularFiltersUsers)) setModularFiltersUsers(saved.modularFiltersUsers);
      if (Array.isArray(saved.appliedModularFiltersUsers)) setAppliedModularFiltersUsers(saved.appliedModularFiltersUsers);

      if (saved.groupTimeFilter) setGroupTimeFilter(saved.groupTimeFilter);
      if (typeof saved.groupSelectedTimePreset === 'string') setGroupSelectedTimePreset(saved.groupSelectedTimePreset);
      if (Array.isArray(saved.groupModularFilters)) setGroupModularFilters(saved.groupModularFilters);
      if (Array.isArray(saved.appliedGroupModularFilters)) setAppliedGroupModularFilters(saved.appliedGroupModularFilters);
    } finally {
      // Let state settle first, then allow persistence to resume.
      setTimeout(() => {
        restoringFromHistoryRef.current = false;
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainContext]);

  // Persist current view state into this history entry WITHOUT navigation.
  // Using `navigate(..., { replace: true })` can remount ActivityPage and cause the
  // "Loading websites..." flash because ActivityPage re-runs its mount effect.
  useEffect(() => {
    if (chainContext !== 'Users') return;
    if (restoringFromHistoryRef.current) return;
    try {
      const current = window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};

      // React Router stores its own state structure; we only merge in our slice.
      const next = {
        ...current,
        usr: {
          ...(current.usr && typeof current.usr === 'object' ? current.usr : {}),
          ...(location.state && typeof location.state === 'object' ? location.state : {}),
          activityUsersState: {
            activeGroupTab,
            searchTerm,
            groupSearchTerm,
            userTimeFilter,
            userSelectedTimePreset,
            modularFiltersUsers,
            appliedModularFiltersUsers,
            groupTimeFilter,
            groupSelectedTimePreset,
            groupModularFilters,
            appliedGroupModularFilters,
          },
        },
      };

      window.history.replaceState(next, document.title);
    } catch (_) {
      // Ignore history write failures (Safari private mode etc.)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chainContext,
    activeGroupTab,
    searchTerm,
    groupSearchTerm,
    userTimeFilter,
    userSelectedTimePreset,
    modularFiltersUsers,
    appliedModularFiltersUsers,
    groupTimeFilter,
    groupSelectedTimePreset,
    groupModularFilters,
    appliedGroupModularFilters,
  ]);
  const {
    loadUsersTableColumns,
    getUsersTableColumns,
    getGroupsTableColumns
  } = usePreferences();

  // teamId for Activity APIs (PostgreSQL team id or name from selectedTeamData)
  // Depend on params.team so it recomputes on team switch — the team switcher
  // updates localStorage before navigating, so reading on URL change is safe.
  const teamId = useMemo(() => {
    try {
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      return selectedTeamData._id || localStorage.getItem("selectedTeam") || "";
    } catch (e) {
      return localStorage.getItem("selectedTeam") || "";
    }
  }, [params.team]);

  const fixedGroupColumnKeySet = useMemo(() => new Set(Object.keys(FIXED_GROUP_COLUMNS)), []);

  const fetchGroupCustomPropertyKeys = useCallback(async () => {
    if (!teamId || !activeGroupTab) {
      setGroupCustomPropertyKeysFromApi([]);
      return;
    }
    try {
      const r = await axiosInstance.get('/group-identities/properties', {
        params: { team_id: teamId, group_key: activeGroupTab }
      });
      if (r.data?.success && Array.isArray(r.data.properties)) {
        setGroupCustomPropertyKeysFromApi(r.data.properties);
      } else {
        setGroupCustomPropertyKeysFromApi([]);
      }
    } catch (err) {
      console.error('Error fetching group custom property keys:', err);
      setGroupCustomPropertyKeysFromApi([]);
    }
  }, [teamId, activeGroupTab]);

  /** Keys offered in Edit columns + used for table headers (API ∪ current rows ∪ saved prefs) */
  const groupAvailableCustomProps = useMemo(() => {
    const s = new Set();
    (groupCustomPropertyKeysFromApi || []).forEach((item) => {
      const k = typeof item === 'string' ? item : item?.key;
      if (k) s.add(k);
    });
    (groups || []).forEach((g) => {
      Object.keys(g.customProperties || {}).forEach((k) => s.add(k));
    });
    if (activeGroupTab) {
      const cfg = getGroupsTableColumns(activeGroupTab);
      [...(cfg.enabled || []), ...(cfg.order || [])].forEach((k) => {
        if (!fixedGroupColumnKeySet.has(k)) s.add(k);
      });
    }
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [
    groupCustomPropertyKeysFromApi,
    groups,
    activeGroupTab,
    getGroupsTableColumns,
    fixedGroupColumnKeySet
  ]);
  
  // Track latest request and cancel in-flight ones to avoid stale updates
  const latestRequestRef = useRef(0);
  const abortControllerRef = useRef(null);
  const sessionIdSearchRef = useRef(null); // Track if we've already searched for a sessionId
  const userIdSearchRef = useRef(null); // Track if we've already searched for a userId

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    fetchUsersWithDateRange(1, term, null, true);
  };



  const handleUserLoadMore = () => {
    if (!loadingMoreUsers && currentPage < totalPages && userTimeFilter) {
      fetchUsersWithDateRange(currentPage + 1, null, null, false);
    }
  };

  const handleGroupLoadMore = () => {
    if (!loadingMoreGroups && currentPage < totalPages && groupTimeFilter && activeGroupTab) {
      fetchGroupsWithDateRange(currentPage + 1, null, null, false);
    }
  };

  // Groups filter bar: time filter change (uses updated_at)
  const handleGroupTimeFilterChange = (filter) => {
    setGroupTimeFilter(filter);
    setGroupSelectedTimePreset(filter.type === 'preset' ? filter.preset : 'custom');
    setCurrentPage(1);
    setGroups([]);
    setTotalGroups(0);
  };

  const handleGroupRefresh = () => {
    setLastGroupRefreshTime(new Date());
    setCurrentPage(1);
    if (activeGroupTab && groupTimeFilter) fetchGroupsWithDateRange(1, null, null, true);
  };

  const handleGroupSearchChange = (term) => {
    setGroupSearchTerm(term);
    setCurrentPage(1);
    if (activeGroupTab && groupTimeFilter) fetchGroupsWithDateRange(1, term, null, true);
  };

  const handleGroupRowClick = (group) => {
    const team = params.team || localStorage.getItem('selectedTeam') || '';
    navigate(
      `/${encodeURIComponent(team)}/activity/groups/${encodeURIComponent(group.groupKey)}/${encodeURIComponent(group.groupId)}`
    );
  };

  const handleGroupTabChange = (groupKey) => {
    setActiveGroupTab(groupKey);
    setGroups([]);
    setTotalGroups(0);
    setCurrentPage(1);
    setGroupSearchTerm('');
    setGroupModularFilters([]);
    setAppliedGroupModularFilters([]);
  };

  const handleGroupModularFiltersChange = (filters) => {
    setGroupModularFilters(filters);
    const noValueOps = ['is_set', 'is_not_set', 'is_true', 'is_false', 'is_numeric', 'is_not_numeric'];
    const validFilters = (filters || []).filter((f) => {
      const src = f.propertySource || f.category;
      if (src === 'cohort') {
        const ids = Array.isArray(f.value) ? f.value : f.value != null ? [f.value] : [];
        return ids.length > 0 && (f.field === 'cohort' || f.property === 'cohort' || ids[0] != null);
      }
      if (!(f.field || f.property) || !f.operator) return false;
      if (!noValueOps.includes(f.operator)) {
        if (f.value === null || f.value === undefined || f.value === '') return false;
        if (Array.isArray(f.value) && f.value.length === 0) return false;
        if ((f.operator === 'between' || f.operator === 'not_between') && (f.value2 === null || f.value2 === undefined || f.value2 === '')) return false;
      }
      return true;
    });
    setAppliedGroupModularFilters(validFilters);
    setCurrentPage(1);
    // Refetch whenever filters change, even if group time preset has not initialized yet.
    if (activeGroupTab) fetchGroupsWithDateRange(1, null, null, true, validFilters);
  };

  // Users filter bar: time filter change (uses last_visit)
  const handleUserTimeFilterChange = (filter) => {
    setUserTimeFilter(filter);
    setUserSelectedTimePreset(filter.type === 'preset' ? filter.preset : 'custom');
    setCurrentPage(1);
    setUsers([]);
    setTotalUsers(0);
    // Intentionally no direct fetch here — setUserTimeFilter triggers the
    // "Initial data fetch for Users" useEffect which fetches with the new filter,
    // avoiding a double-fetch / "canceled" race condition.
  };

  // Users filter bar: refresh
  const handleUserRefresh = () => {
    setLastUserRefreshTime(new Date());
    setCurrentPage(1);
    fetchUsersWithDateRange(1, null, null, true);
  };

  // A row opens the user drawer over the list.
  const handleUserRowClick = (user) => {
    const uid = user.distinctId || user.userId;
    if (uid) openUserProfile(uid);
  };

  // ?userId= opens that user's drawer once the list has loaded
  useEffect(() => {
    const userIdFromUrl = new URLSearchParams(location.search).get('userId');
    if (!userIdFromUrl || chainContext !== 'Users' || !users.length || loading) return;
    const matching = users.find(u =>
      (u.distinctId || u.userId) === userIdFromUrl ||
      (u.distinctId || u.userId)?.toLowerCase() === userIdFromUrl?.toLowerCase()
    );
    if (matching) openUserProfile(matching.distinctId || matching.userId);
  }, [chainContext, users, loading, location.search]);

  // Handle URL changes - navigate when chain context should change
  useEffect(() => {
    // /activity → /activity/events. Events is the default grain now that the
    // two share one sidebar entry; sessions is a tab you switch into.
    if (location.pathname.endsWith('/activity') && !params.subpage) {
      const team = params.team || localStorage.getItem("selectedTeam") || '';
      navigate(`/${encodeURIComponent(team)}/activity/events`, { replace: true });
    }
  }, [location.pathname, params.subpage, params.team, navigate]);




  // Handle userId query parameter - filter to show only that user (for Users view)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const userIdParam = searchParams.get('userId');
    
    if (userIdParam && chainContext === 'Users' && teamId) {
      if (userIdSearchRef.current !== userIdParam) {
        userIdSearchRef.current = userIdParam;
      }
    } else if (!userIdParam) {
      userIdSearchRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, chainContext, teamId]);
  
  // Reset state when chain context changes (via URL)
  useEffect(() => {
    setCurrentPage(1);
    // Don't reset searchTerm if we have query parameters in URL (to preserve the filter)
    const searchParams = new URLSearchParams(location.search);
    const sessionIdParam = searchParams.get('sessionId');
    const userIdParam = searchParams.get('userId');
    const searchParam = searchParams.get('search');
    const eventNameParam = searchParams.get('eventName');
    if (!sessionIdParam && !userIdParam && !searchParam && !eventNameParam) {
      setSearchTerm('');
      sessionIdSearchRef.current = null;
      userIdSearchRef.current = null;
    }
    setError(null);
    setLoading(false);
    
    setUsers([]);
    setTotalUsers(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainContext]);

  // Default window when no explicit filter is set — last 30 days (matches the
  // selected preset on initial mount so the API gets a real window).
  const defaultTimeRangeParams = () => {
    const now = new Date();
    const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: now.toISOString() };
  };


  // Build from/to for groups API (updated_at filter)
  const getGroupsTimeRangeParams = (timeFilterOverride = null) => {
    const filterToUse = timeFilterOverride !== null ? timeFilterOverride : groupTimeFilter;
    if (!filterToUse) return defaultTimeRangeParams();
    return {
      from: new Date(filterToUse.from).toISOString(),
      to: new Date(filterToUse.to).toISOString()
    };
  };

  // Build from/to for users API (last_visit filter)
  const getUsersTimeRangeParams = (timeFilterOverride = null) => {
    const filterToUse = timeFilterOverride !== null ? timeFilterOverride : userTimeFilter;
    if (!filterToUse) return defaultTimeRangeParams();
    const fromDate = new Date(filterToUse.from);
    const toDate = new Date(filterToUse.to);
    return {
      from: fromDate.toISOString(),
      to: toDate.toISOString()
    };
  };

  // Fetch users for Users view. isReset = replace list; !isReset = append (Load More). Uses last_visit for time filter.
  const fetchUsersWithDateRange = async (page = 1, searchOverride = null, timeFilterOverride = null, isReset = true, filtersOverride = null) => {
    if (!teamId) {
      setUsers([]);
      setTotalUsers(0);
      setTotalPages(1);
      setLoading(false);
      setLoadingMoreUsers(false);
      return;
    }

    let requestId = 0;
    try {
      requestId = ++latestRequestRef.current;
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch (_) {}
      }
      abortControllerRef.current = new AbortController();

      if (isReset) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMoreUsers(true);
      }

      const timeRange = getUsersTimeRangeParams(timeFilterOverride);
      const params = {
        team_id: teamId,
        page: page.toString(),
        limit: '25'
      };
      if (timeRange.from) params.from = timeRange.from;
      if (timeRange.to) params.to = timeRange.to;

      const currentSearchTerm = searchOverride !== null ? searchOverride : searchTerm;
      if (currentSearchTerm && currentSearchTerm.trim()) {
        params.search = currentSearchTerm.trim();
      }

      const filtersToUse = Array.isArray(filtersOverride) ? filtersOverride : (appliedModularFiltersUsers || []);
      if (filtersToUse.length > 0) {
        params.modularFilters = JSON.stringify(filtersToUse);
      }

      // React Query cache key for instant back/forward UX
      const usersCacheKey = [
        'activity',
        'users',
        teamId,
        timeRange.from || null,
        timeRange.to || null,
        currentSearchTerm?.trim() || '',
        filtersToUse,
      ];

      // Hydrate list immediately from cache for the first page/reset fetch
      if (isReset && page === 1) {
        const cached = queryClient.getQueryData(usersCacheKey);
        if (cached && typeof cached === 'object') {
          setUsers(cached.users || []);
          setTotalUsers(cached.total || 0);
          setTotalPages(cached.totalPages || 1);
          setCurrentPage(1);
          setLoading(false);
        }
      }

      const response = await axiosInstance.get('/user-identities', {
        params,
        signal: abortControllerRef.current.signal
      });

      if (requestId !== latestRequestRef.current) {
        return;
      }

      if (response.data && response.data.success) {
        const fetchedUsers = response.data.users || [];
        const total = response.data.total || 0;
        const totalPagesCalc = response.data.totalPages || Math.max(1, Math.ceil(total / 25));

        if (isReset) {
          setUsers(fetchedUsers);
        } else {
          setUsers(prev => [...prev, ...fetchedUsers]);
        }
        setTotalPages(totalPagesCalc);
        setCurrentPage(page);
        setTotalUsers(total);

        // Cache the first page result for this exact query (tab/filter/search/time range)
        if (page === 1) {
          queryClient.setQueryData(usersCacheKey, {
            users: fetchedUsers,
            total,
            totalPages: totalPagesCalc,
            fetchedAt: Date.now(),
          });
        }
      } else {
        if (isReset) setUsers([]);
        setTotalPages(1);
        setTotalUsers(0);
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || 'Failed to fetch users');
      if (isReset) setUsers([]);
      setTotalPages(1);
      setTotalUsers(0);
    } finally {
      if (latestRequestRef.current === requestId) {
        if (isReset) setLoading(false);
        else setLoadingMoreUsers(false);
      }
    }
  };

  // Fetch groups for a specific group_key tab. isReset = replace list; !isReset = append.
  const fetchGroupsWithDateRange = async (page = 1, searchOverride = null, timeFilterOverride = null, isReset = true, filtersOverride = null) => {
    if (!teamId || !activeGroupTab) {
      setGroups([]);
      setTotalGroups(0);
      setTotalPages(1);
      if (isReset) setLoading(false);
      else setLoadingMoreGroups(false);
      return;
    }

    let requestId = 0;
    try {
      requestId = ++latestRequestRef.current;
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch (_) {}
      }
      abortControllerRef.current = new AbortController();

      if (isReset) { setLoading(true); setError(null); }
      else setLoadingMoreGroups(true);

      const timeRange = getGroupsTimeRangeParams(timeFilterOverride);
      const reqParams = {
        team_id: teamId,
        group_key: activeGroupTab,
        page: page.toString(),
        limit: '25'
      };
      if (timeRange.from) reqParams.from = timeRange.from;
      if (timeRange.to) reqParams.to = timeRange.to;

      const currentSearch = searchOverride !== null ? searchOverride : groupSearchTerm;
      if (currentSearch && currentSearch.trim()) reqParams.search = currentSearch.trim();

      const filtersToUse = Array.isArray(filtersOverride) ? filtersOverride : (appliedGroupModularFilters || []);
      if (filtersToUse.length > 0) reqParams.modularFilters = JSON.stringify(filtersToUse);

      const groupsCacheKey = [
        'activity',
        'groups',
        teamId,
        activeGroupTab,
        timeRange.from || null,
        timeRange.to || null,
        currentSearch?.trim() || '',
        filtersToUse,
      ];

      if (isReset && page === 1) {
        const cached = queryClient.getQueryData(groupsCacheKey);
        if (cached && typeof cached === 'object') {
          setGroups(cached.groups || []);
          setTotalGroups(cached.total || 0);
          setTotalPages(cached.totalPages || 1);
          setCurrentPage(1);
          setLoading(false);
        }
      }

      const response = await axiosInstance.get('/group-identities', {
        params: reqParams,
        signal: abortControllerRef.current.signal
      });

      if (requestId !== latestRequestRef.current) return;

      if (response.data && response.data.success) {
        const fetchedGroups = response.data.groups || [];
        const total = response.data.total || 0;
        const totalPagesCalc = response.data.totalPages || Math.max(1, Math.ceil(total / 25));

        if (isReset) setGroups(fetchedGroups);
        else setGroups(prev => [...prev, ...fetchedGroups]);
        setTotalPages(totalPagesCalc);
        setCurrentPage(page);
        setTotalGroups(total);

        if (page === 1) {
          queryClient.setQueryData(groupsCacheKey, {
            groups: fetchedGroups,
            total,
            totalPages: totalPagesCalc,
            fetchedAt: Date.now(),
          });
        }
      } else {
        if (isReset) setGroups([]);
        setTotalPages(1);
        setTotalGroups(0);
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error fetching groups:', err);
      setError(err.response?.data?.message || 'Failed to fetch groups');
      if (isReset) setGroups([]);
      setTotalPages(1);
      setTotalGroups(0);
    } finally {
      if (latestRequestRef.current === requestId) {
        if (isReset) setLoading(false);
        else setLoadingMoreGroups(false);
      }
    }
  };

  // Users filters change: apply dynamically (no Apply button). Validates, updates state, refetches.
  const handleModularFiltersUsers = (filters) => {
    setModularFiltersUsers(filters);
    const noValueOperators = ['is_set', 'is_not_set', 'is_true', 'is_false', 'is_numeric', 'is_not_numeric'];
    const validFilters = (filters || []).filter(f => {
      if (!(f.field || f.property) || !f.operator) return false;
      if (!noValueOperators.includes(f.operator)) {
        if (f.value === null || f.value === undefined || f.value === '') return false;
        if (Array.isArray(f.value) && f.value.length === 0) return false;
        if ((f.operator === 'between' || f.operator === 'not_between') && (f.value2 === null || f.value2 === undefined || f.value2 === '')) return false;
      }
      return true;
    });
    setAppliedModularFiltersUsers(validFilters);
    setCurrentPage(1);
    fetchUsersWithDateRange(1, null, null, true, validFilters);
  };






  // Initial data fetch for Users (after time filter is set)
  useEffect(() => {
    if (chainContext === 'Users' && userTimeFilter) {
      setCurrentPage(1);
      const userIdParam = new URLSearchParams(location.search).get('userId');
      if (userIdParam) setSearchTerm(userIdParam);
      fetchUsersWithDateRange(1, userIdParam || null, null, true);
    }
  }, [chainContext, userTimeFilter, location.search]);



  // Load users table columns when Users
  useEffect(() => {
    if (chainContext === 'Users' && selectedTeam) {
      loadUsersTableColumns(selectedTeam);
    }
  }, [chainContext, selectedTeam, loadUsersTableColumns]);

  // Initialize user time filter to last 30 days when Users (once)
  const userTimeFilterInitialized = useRef(false);
  useEffect(() => {
    if (chainContext === 'Users' && selectedTeam && !userTimeFilterInitialized.current) {
      userTimeFilterInitialized.current = true;
      const now = new Date();
      const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setUserTimeFilter({
        type: 'preset',
        preset: '30d',
        from: from.toISOString(),
        to: now.toISOString()
      });
      setUserSelectedTimePreset('30d');
    }
    if (chainContext !== 'Users') {
      userTimeFilterInitialized.current = false;
    }
  }, [chainContext, selectedTeam]);

  // Fetch group_keys when entering Users context
  useEffect(() => {
    if (chainContext === 'Users' && teamId) {
      const keysCacheKey = ['activity', 'groupKeys', teamId];

      // Instant render from cache (fast back/forward + reduces tab load lag)
      const cached = queryClient.getQueryData(keysCacheKey);
      if (Array.isArray(cached)) {
        setGroupKeys(cached);
      }

      axiosInstance
        .get('/group-identities/keys', { params: { team_id: teamId } })
        .then((r) => {
          const keys = r.data.keys || [];
          setGroupKeys(keys);
          queryClient.setQueryData(keysCacheKey, keys);
        })
        .catch(() => setGroupKeys([]));
    }
    if (chainContext !== 'Users') {
      setGroupKeys([]);
      setActiveGroupTab(null);
    }
  }, [chainContext, teamId]);

  // Persist the selected group tab via URL: ?groupKey=team (etc).
  // Used when navigating back from GroupDetailPage so the UX returns to the groups view.
  useEffect(() => {
    if (chainContext !== 'Users') return;
    const searchParams = new URLSearchParams(location.search);
    const groupKeyParam = (searchParams.get('groupKey') || '').trim();
    if (!groupKeyParam) return;
    // Only activate if this group key exists for the team; otherwise ignore.
    if (Array.isArray(groupKeys) && groupKeys.some((k) => k?.group_key === groupKeyParam)) {
      if (activeGroupTab !== groupKeyParam) {
        handleGroupTabChange(groupKeyParam);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainContext, location.search, groupKeys]);

  // Distinct custom property keys for the active group_key (Edit columns + table headers)
  useEffect(() => {
    if (chainContext === 'Users' && activeGroupTab && teamId) {
      fetchGroupCustomPropertyKeys();
    } else {
      setGroupCustomPropertyKeysFromApi([]);
    }
  }, [chainContext, activeGroupTab, teamId, fetchGroupCustomPropertyKeys]);

  useEffect(() => {
    if (isGroupColumnModalOpen && chainContext === 'Users' && activeGroupTab && teamId) {
      fetchGroupCustomPropertyKeys();
    }
  }, [isGroupColumnModalOpen, chainContext, activeGroupTab, teamId, fetchGroupCustomPropertyKeys]);

  // Initialize group time filter to last 30 days when a group tab becomes active (once per tab)
  const groupTimeFilterInitialized = useRef(false);
  useEffect(() => {
    if (chainContext === 'Users' && activeGroupTab && selectedTeam && !groupTimeFilterInitialized.current) {
      groupTimeFilterInitialized.current = true;
      const now = new Date();
      const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setGroupTimeFilter({
        type: 'preset',
        preset: '30d',
        from: from.toISOString(),
        to: now.toISOString()
      });
      setGroupSelectedTimePreset('30d');
    }
    if (!activeGroupTab) {
      groupTimeFilterInitialized.current = false;
    }
  }, [chainContext, activeGroupTab, selectedTeam]);

  // Fetch groups when activeGroupTab or groupTimeFilter changes
  useEffect(() => {
    if (chainContext === 'Users' && activeGroupTab && groupTimeFilter) {
      setCurrentPage(1);
      fetchGroupsWithDateRange(1, null, null, true);
    }
  }, [chainContext, activeGroupTab, groupTimeFilter]);


  // Update document title when search is active
  useEffect(() => {
    if (searchTerm) {
      document.title = `Search: "${searchTerm}" - ${chainContext} | Trodo`;
    } else {
      document.title = `${chainContext} | Trodo`;
    }
  }, [searchTerm, chainContext]);

  // Check if team is selected
  if (!selectedTeam) {
    return (
      <div className="flex-grow overflow-y-auto p-6">
        <div className="rounded-lg border border-[color:var(--border-default)] bg-surface p-6 shadow-[0_1px_0_0_rgba(26,26,25,0.04)]">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--trodo-primary)]">
                <svg className="h-5 w-5 text-ink-onBrand" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-[color:var(--text-primary)]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Please select a team to view sessions.
              </p>
              <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                You can select a team from the sidebar or settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page rides Dashboard's outer scroll (same as Agent runs). No
          flex-1 / no overflow constraints here: those would size the page
          to the viewport and prevent the outer scroll from picking up
          overflowing content. The drawer's `absolute inset-0` resolves to
          Dashboard's `relative` white panel, same as AgentRunDetailPage. */}
      <div>
        <div className="bg-surface">
          {chainContext === 'Users' ? (
            <>
              {activeGroupTab === null ? (
                <>
                  <UsersFilterBar
                    onTimeFilterChange={handleUserTimeFilterChange}
                    onSearchChange={handleSearch}
                    searchTerm={searchTerm}
                    selectedTimePreset={userSelectedTimePreset}
                    onEditColumns={() => setIsUserColumnModalOpen(true)}
                    filters={modularFiltersUsers}
                    onFiltersChange={handleModularFiltersUsers}
                    teamId={selectedTeam}
                    groupKeys={groupKeys}
                    activeGroupTab={activeGroupTab}
                    onGroupTabChange={handleGroupTabChange}
                    totalsSlot={!searchTerm ? (
                      <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
                        <div className="flex items-center gap-1.5 px-3 text-[12px]">
                          <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">
                            {totalUsers.toLocaleString()}
                          </span>
                          <span className="text-[color:var(--text-muted)]">total users</span>
                        </div>
                      </div>
                    ) : null}
                  />
                  <div className="flex shrink-0 flex-col gap-3 px-4 pb-8 pt-3">
                    <UsersTable
                      users={users}
                      loading={loading}
                      loadingMore={loadingMoreUsers}
                      error={error}
                      hasMore={currentPage < totalPages}
                      onLoadMore={handleUserLoadMore}
                      hasSearchFilter={!!searchTerm}
                      searchTerm={searchTerm}
                      columnConfig={getUsersTableColumns()}
                      onRowClick={handleUserRowClick}
                      hideLoadMore
                    />
                    <TableFooter
                      shown={users.length}
                      total={totalUsers}
                      hasMore={currentPage < totalPages && !searchTerm && users.length > 0}
                      loading={loadingMoreUsers}
                      onLoadMore={handleUserLoadMore}
                      note={(() => {
                        if (loading && users.length === 0) return 'Loading…';
                        if (totalUsers === 0 && users.length === 0) return 'No users match';
                        return null;
                      })()}
                    />
                  </div>
                </>
              ) : (
                <>
                  <GroupsFilterBar
                    onRefresh={handleGroupRefresh}
                    onTimeFilterChange={(filter) => {
                      setGroupTimeFilter(filter);
                      setGroupSelectedTimePreset(filter.type === 'preset' ? filter.preset : 'custom');
                      setCurrentPage(1);
                      setGroups([]);
                      setTotalGroups(0);
                    }}
                    onSearchChange={handleGroupSearchChange}
                    searchTerm={groupSearchTerm}
                    selectedTimePreset={groupSelectedTimePreset}
                    lastRefreshTime={lastGroupRefreshTime}
                    showTitle={true}
                    showRefresh={false}
                    onEditColumns={() => setIsGroupColumnModalOpen(true)}
                    filters={groupModularFilters}
                    onFiltersChange={handleGroupModularFiltersChange}
                    teamId={selectedTeam}
                    groupKey={activeGroupTab}
                    groupKeys={groupKeys}
                    activeGroupTab={activeGroupTab}
                    onGroupTabChange={handleGroupTabChange}
                  />
                  <div className="flex shrink-0 flex-col gap-3 px-4 pb-8 pt-3">
                    <GroupsTable
                      groups={groups}
                      loading={loading}
                      loadingMore={loadingMoreGroups}
                      error={error}
                      totalGroups={totalGroups}
                      hasMore={currentPage < totalPages}
                      onLoadMore={handleGroupLoadMore}
                      columnConfig={getGroupsTableColumns(activeGroupTab)}
                      onRowClick={handleGroupRowClick}
                      groupKey={activeGroupTab}
                      knownCustomPropKeys={groupAvailableCustomProps}
                    />
                  </div>
                </>
              )}
            </>
          ) : null}
        </div>

      </div>

      {/* User column config modal (Users only, Users tab) */}
      {chainContext === 'Users' && activeGroupTab === null && (
        <UserColumnConfigModal
          isOpen={isUserColumnModalOpen}
          onClose={() => setIsUserColumnModalOpen(false)}
          teamId={selectedTeam}
        />
      )}
      {/* Group column config modal (Users context, group tab active) */}
      {chainContext === 'Users' && activeGroupTab !== null && (
        <GroupColumnConfigModal
          isOpen={isGroupColumnModalOpen}
          onClose={() => setIsGroupColumnModalOpen(false)}
          teamId={selectedTeam}
          groupKey={activeGroupTab}
          availableCustomProps={groupAvailableCustomProps}
        />
      )}
    </div>
  );
};

export default Sessions;
